import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

interface Track {
  id: string;
  label: string;
  icon: string;
  staticUrl: string | null;
  minioKey: string | null;
  mime: string | null;
  sizeBytes: number | null;
  sortOrder: number;
  enabled: boolean;
  createdAt: string;
}

const ICON_OPTIONS = [
  'Volume2', 'CloudRain', 'Wind', 'Coffee', 'Radio', 'Music', 'Music2', 'Music3', 'Music4',
  'Headphones', 'Flame', 'Waves', 'Bird', 'TreeDeciduous', 'Mountain', 'Snowflake', 'Zap',
];

export function FocusAudioPage() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('Volume2');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const data = await api<{ tracks: Track[] }>('/admin/focus-audio');
      setTracks(data.tracks);
    } catch (e: any) {
      setErr(e?.message ?? 'load_failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function upload(e: React.FormEvent) {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('label', label || file.name);
      fd.append('icon', icon);
      const res = await fetch('/api/admin/focus-audio', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson?.error ?? `http_${res.status}`);
      }
      setUploadMsg({ tone: 'ok', text: 'Faixa adicionada.' });
      setLabel('');
      setIcon('Volume2');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e: any) {
      setUploadMsg({ tone: 'err', text: `Falha: ${e?.message ?? 'erro'}` });
    } finally {
      setUploading(false);
    }
  }

  async function patch(id: string, body: Partial<Track>) {
    try {
      await api(`/admin/focus-audio/${id}`, { method: 'PATCH', body });
      await load();
    } catch (e: any) {
      setErr(e?.message ?? 'patch_failed');
    }
  }

  async function remove(id: string) {
    if (!confirm('Excluir esta faixa? Ação irreversível.')) return;
    try {
      await api(`/admin/focus-audio/${id}`, { method: 'DELETE' });
      await load();
    } catch (e: any) {
      setErr(e?.message ?? 'delete_failed');
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <h1 className="text-xl font-semibold">Sons de Foco</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Faixas disponíveis no menu "Sons de Foco" dentro da plataforma. Arquivos enviados ficam no MinIO.
        </p>
      </header>

      {/* Upload form */}
      <section className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
        <h2 className="text-sm font-semibold mb-4">Adicionar nova faixa</h2>
        <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-3 items-end">
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            <span>Nome</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Chuva intensa"
              className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 outline-none focus:border-zinc-600"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400">
            <span>Ícone (lucide)</span>
            <select
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 outline-none focus:border-zinc-600"
            >
              {ICON_OPTIONS.map((i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-zinc-400 md:col-span-2">
            <span>Arquivo de áudio (mp3, wav, ogg, m4a — máx 25MB)</span>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 file:mr-3 file:rounded file:border-0 file:bg-zinc-800 file:px-3 file:py-1 file:text-zinc-100"
            />
          </label>
          <button
            type="submit"
            disabled={!file || uploading}
            className="md:col-span-1 self-end bg-rose-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded px-4 py-2 text-sm font-medium hover:bg-rose-500 transition-colors"
          >
            {uploading ? 'Enviando…' : 'Enviar'}
          </button>
        </form>
        {uploadMsg && (
          <p className={`mt-3 text-xs ${uploadMsg.tone === 'ok' ? 'text-emerald-400' : 'text-rose-400'}`}>
            {uploadMsg.text}
          </p>
        )}
      </section>

      {/* Track list */}
      <section className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Biblioteca</h2>
          <span className="text-xs text-zinc-500">{tracks.length} faixa(s)</span>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-zinc-500">Carregando…</div>
        ) : err ? (
          <div className="p-6 text-sm text-rose-400">Erro: {err}</div>
        ) : tracks.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500">Nenhuma faixa cadastrada.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {tracks.map((t) => (
              <li key={t.id} className="p-4 grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center">
                <div className="text-xs text-zinc-500 font-mono w-6 text-center">{t.sortOrder}</div>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{t.label}</div>
                  <div className="text-xs text-zinc-500 truncate flex items-center gap-2">
                    <span>{t.icon}</span>
                    <span>·</span>
                    <span>{t.staticUrl ? 'static' : 'MinIO'}</span>
                    {t.sizeBytes != null && (
                      <>
                        <span>·</span>
                        <span>{(t.sizeBytes / 1024 / 1024).toFixed(2)}MB</span>
                      </>
                    )}
                  </div>
                </div>
                <audio
                  src={t.staticUrl ?? `/api/focus-audio/${t.id}/serve`}
                  controls
                  preload="none"
                  className="h-8 max-w-[220px]"
                />
                <button
                  onClick={() => patch(t.id, { enabled: !t.enabled })}
                  className={`text-xs px-2.5 py-1 rounded border ${
                    t.enabled
                      ? 'border-emerald-700/60 text-emerald-300 bg-emerald-500/10'
                      : 'border-zinc-700 text-zinc-400 bg-zinc-900'
                  }`}
                >
                  {t.enabled ? 'Ativo' : 'Inativo'}
                </button>
                <button
                  onClick={() => remove(t.id)}
                  className="text-xs px-2.5 py-1 rounded border border-rose-800/60 text-rose-300 bg-rose-500/10 hover:bg-rose-500/20"
                >
                  Excluir
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
